import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { InputSwitch } from 'primereact/inputswitch';
import { url } from '../../../../env';
import { AppButton, AppTextField, AppIconButton } from '../../../../Components/FormKit';
import { formatDate } from './roleConfig';

const UserProfileTab = ({ user, toast, onUpdate }) => {
    const [form, setForm] = useState({
        name: user?.name ?? '',
        email: user?.email ?? '',
        is_active: !!user?.is_active,
    });
    const [saving, setSaving] = useState(false);
    const [togglingActive, setTogglingActive] = useState(false);

    useEffect(() => {
        setForm({
            name: user?.name ?? '',
            email: user?.email ?? '',
            is_active: !!user?.is_active,
        });
    }, [user]);

    const dirty =
        form.name !== (user?.name ?? '') ||
        form.email !== (user?.email ?? '') ||
        form.is_active !== !!user?.is_active;

    const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

    const copyToken = () => {
        if (!user?.external_token) return;
        navigator.clipboard.writeText(user.external_token);
        toast?.current?.show({
            severity: 'success',
            summary: 'Copiado al portapapeles',
            life: 1500,
        });
    };

    const persist = async (payload, successMsg) => {
        const res = await axios.put(`${url}/users/${user.id}`, payload, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        toast?.current?.show({ severity: 'success', summary: successMsg, life: 2500 });
        return res;
    };

    const onSave = async () => {
        if (!dirty) return;
        setSaving(true);
        try {
            await persist({
                name: form.name,
                email: form.email,
                is_active: form.is_active,
            }, 'Cambios guardados');
            onUpdate?.();
        } catch (e) {
            console.error(e);
            toast?.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: e?.response?.data?.message ?? 'No se pudo guardar.',
                life: 4000,
            });
        } finally {
            setSaving(false);
        }
    };

    const onToggleActive = async () => {
        setTogglingActive(true);
        try {
            const next = !user.is_active;
            await persist({ is_active: next }, next ? 'Usuario reactivado' : 'Usuario desactivado');
            onUpdate?.();
        } catch (e) {
            console.error(e);
            toast?.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'No se pudo cambiar el estado.',
                life: 4000,
            });
        } finally {
            setTogglingActive(false);
        }
    };

    return (
        <div>
            <div className="udm-grid-2">
                <AppTextField
                    label="Nombre"
                    value={form.name}
                    onChange={(e) => set('name', e.target.value)}
                />
                <AppTextField
                    label="Email"
                    value={form.email}
                    onChange={(e) => set('email', e.target.value)}
                />

                <div className="udm-readonly-field">
                    <span className="udm-readonly-label">Fecha de alta</span>
                    <span className="udm-readonly-value">{formatDate(user?.created_at)}</span>
                </div>

                {user?.external_token && (
                    <div className="udm-readonly-field" style={{ gridColumn: '1 / -1' }}>
                        <span className="udm-readonly-label">External token</span>
                        <div className="udm-token-row">
                            <span className="udm-token">{user.external_token}</span>
                            <AppIconButton
                                icon="pi pi-copy"
                                onClick={copyToken}
                                title="Copiar"
                                variant="secondary"
                                round
                            />
                        </div>
                    </div>
                )}
            </div>

            <div className="udm-switch-row">
                <div className="udm-switch-text">
                    <span className="udm-switch-title">Usuario activo</span>
                    <span className="udm-switch-help">
                        Si se desactiva, el usuario no podrá iniciar sesión.
                    </span>
                </div>
                <InputSwitch
                    checked={form.is_active}
                    onChange={(e) => set('is_active', e.value)}
                />
            </div>

            <div className="udm-footer">
                <AppButton
                    label={user?.is_active ? 'Desactivar usuario' : 'Reactivar usuario'}
                    variant="secondary"
                    onClick={onToggleActive}
                    loading={togglingActive}
                    style={user?.is_active ? {
                        borderColor: '#ef4444',
                        color: '#ef4444',
                    } : {
                        borderColor: '#00BA6E',
                        color: '#00BA6E',
                    }}
                />
                <AppButton
                    label="Guardar cambios"
                    variant="primary"
                    onClick={onSave}
                    disabled={!dirty || saving}
                    loading={saving}
                />
            </div>
        </div>
    );
};

export default UserProfileTab;
